import type { SelectedProduct } from "@credit-acceptance/step6-bff-client";
import { zodResolver } from "@hookform/resolvers/zod";
import { useController, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Form } from "../../../../../components/ui/form";
import { SubsectionHeader } from "../../../../../components/ui/subsectionHeader";
import { CheckboxField, CurrencyField, DateField, DropdownField, TextField } from "./Fields";
import {
  buildVscDefaultValues,
  FORM_NUMBER_CODES,
  FORM_NUMBER_NOT_LISTED,
  FORM_NUMBER_SELECT_AN_OPTION,
  TPAP_VENDOR_OPTIONS,
  UNLIMITED_MILE_SENTINEL,
  UNLIMITED_MONTH_SENTINEL,
  vehicleServiceContractInformationSchema,
} from "./VehicleServiceContractInformation.schema";

interface VehicleServiceContractInformationProperties {
  readonly vsc?: SelectedProduct;
  /** Whether every field below is typeable/selectable — condition TBD; defaults to disabled (today's behavior). */
  readonly editable?: boolean;
}

/**
 * RHF + Zod, same pattern as ContractVehicleInformation.tsx/InsuranceInformation.tsx
 * — see VehicleServiceContractInformation.schema.ts for the validation rules
 * and for what's mocked vs. sourced from the real `SelectedProduct` prop.
 *
 * Not registered with `useRegisterSaveableSection`, same reasoning as
 * InsuranceInformation: `ContractVehicleInformation` already owns the
 * `DashboardSaveV2Request.vehicle` key, and the shared save orchestrator's
 * merge is shallow/last-wins, so a second section writing to `vehicle` would
 * silently clobber one or the other's data.
 */
export function VehicleServiceContractInformation({
  vsc,
  editable = false,
}: VehicleServiceContractInformationProperties) {
  const { t } = useTranslation();
  const disabled = !editable;
  const form = useForm({
    resolver: zodResolver(vehicleServiceContractInformationSchema),
    mode: "onBlur",
    defaultValues: buildVscDefaultValues(vsc),
  });
  const { control } = form;
  const tpapVsc = useController({ control, name: "tpapVsc" });
  const vendorDisplay = useController({ control, name: "vendorDisplay" });
  const tpapVendor = useController({ control, name: "tpapVendor" });
  const certificateNumber = useController({ control, name: "certificateNumber" });
  const sellingPrice = useController({ control, name: "sellingPrice" });
  const termDisplay = useController({ control, name: "termDisplay" });
  const isUnlimitedMonth = useController({ control, name: "isUnlimitedMonth" });
  const termMonths = useController({ control, name: "termMonths" });
  const isUnlimitedMile = useController({ control, name: "isUnlimitedMile" });
  const termMiles = useController({ control, name: "termMiles" });
  const effectiveDate = useController({ control, name: "effectiveDate" });
  const formNumber = useController({ control, name: "formNumber" });

  const tpapVendorOptions = TPAP_VENDOR_OPTIONS.map((vendor) => ({ value: vendor, label: vendor }));
  const formNumberOptions = [
    { value: FORM_NUMBER_SELECT_AN_OPTION, label: t("vehicle.vehicleServiceContract.selectAnOption") },
    ...FORM_NUMBER_CODES.map((code) => ({ value: code, label: code })),
    { value: FORM_NUMBER_NOT_LISTED, label: t("vehicle.vehicleServiceContract.formNotListed") },
  ];

  // Changing the TPAP vendor resets Form Number back to unset (no vendor-scoped
  // form list/correction-letter flag exists here — see the schema's comment).
  function handleTpapVendorChange(value: string) {
    tpapVendor.field.onChange(value);
    formNumber.field.onChange(FORM_NUMBER_SELECT_AN_OPTION);
    void form.trigger("formNumber");
  }

  // Validates on change rather than blur (like CPO Date) — selecting an
  // option doesn't blur the trigger, so `mode: "onBlur"` alone would never
  // reveal the required error.
  function handleFormNumberChange(value: string) {
    formNumber.field.onChange(value);
    void form.trigger("formNumber");
  }

  // Checking sets Term Months to the "999" sentinel; unchecking wipes
  // whatever was typed back to blank.
  function handleUnlimitedMonthChange(checked: boolean) {
    isUnlimitedMonth.field.onChange(checked);
    termMonths.field.onChange(checked ? UNLIMITED_MONTH_SENTINEL : "");
    void form.trigger("termMonths");
  }

  // Same wipe-on-uncheck behavior as the month checkbox, sentinel "999999".
  function handleUnlimitedMileChange(checked: boolean) {
    isUnlimitedMile.field.onChange(checked);
    termMiles.field.onChange(checked ? UNLIMITED_MILE_SENTINEL : "");
    void form.trigger("termMiles");
  }

  return (
    <div>
      <SubsectionHeader>{t("vehicle.vehicleServiceContract.heading")}</SubsectionHeader>
      <Form {...form}>
        <form onSubmit={(event) => event.preventDefault()} noValidate>
          <div className="gap-(--spacing-4) px-(--spacing-3) py-(--spacing-3) grid grid-cols-2">
            {!tpapVsc.field.value && (
              <TextField
                label={t("vehicle.vehicleServiceContract.vendor")}
                value={vendorDisplay.field.value}
                disabled
              />
            )}
            <TextField
              label={t("vehicle.vehicleServiceContract.certificateNumber")}
              value={certificateNumber.field.value}
              onChange={certificateNumber.field.onChange}
              onBlur={certificateNumber.field.onBlur}
              error={certificateNumber.fieldState.error}
              disabled={disabled}
            />
            <CurrencyField
              label={t("vehicle.vehicleServiceContract.sellingPrice")}
              value={sellingPrice.field.value}
              onChange={sellingPrice.field.onChange}
              onBlur={sellingPrice.field.onBlur}
              error={sellingPrice.fieldState.error}
              disabled={disabled || !tpapVsc.field.value}
            />
            {!tpapVsc.field.value && (
              <TextField label={t("vehicle.vehicleServiceContract.term")} value={termDisplay.field.value} disabled />
            )}
            {tpapVsc.field.value && (
              <>
                <DropdownField
                  label={t("vehicle.vehicleServiceContract.vendor")}
                  value={tpapVendor.field.value}
                  onChange={handleTpapVendorChange}
                  options={tpapVendorOptions}
                  disabled={disabled}
                />
                <div className="gap-(--spacing-2) flex flex-col">
                  {isUnlimitedMonth.field.value ? (
                    <TextField label={t("vehicle.vehicleServiceContract.termMonths")} value="" disabled />
                  ) : (
                    <TextField
                      label={t("vehicle.vehicleServiceContract.termMonths")}
                      value={termMonths.field.value}
                      onChange={termMonths.field.onChange}
                      onBlur={termMonths.field.onBlur}
                      error={termMonths.fieldState.error}
                      disabled={disabled}
                    />
                  )}
                  <CheckboxField
                    label={t("vehicle.vehicleServiceContract.unlimitedMonth")}
                    checked={isUnlimitedMonth.field.value}
                    onCheckedChange={handleUnlimitedMonthChange}
                    disabled={disabled}
                  />
                </div>
                <div className="gap-(--spacing-2) flex flex-col">
                  {isUnlimitedMile.field.value ? (
                    <TextField label={t("vehicle.vehicleServiceContract.termMiles")} value="" disabled />
                  ) : (
                    <TextField
                      label={t("vehicle.vehicleServiceContract.termMiles")}
                      value={termMiles.field.value}
                      onChange={termMiles.field.onChange}
                      onBlur={termMiles.field.onBlur}
                      error={termMiles.fieldState.error}
                      disabled={disabled}
                    />
                  )}
                  <CheckboxField
                    label={t("vehicle.vehicleServiceContract.unlimitedMile")}
                    checked={isUnlimitedMile.field.value}
                    onCheckedChange={handleUnlimitedMileChange}
                    disabled={disabled}
                  />
                </div>
              </>
            )}
            <DateField
              label={t("vehicle.vehicleServiceContract.effectiveDate")}
              value={effectiveDate.field.value}
              onChange={effectiveDate.field.onChange}
              onBlur={effectiveDate.field.onBlur}
              error={effectiveDate.fieldState.error}
              disabled={disabled || !tpapVsc.field.value}
            />
            {tpapVsc.field.value && (
              <DropdownField
                label={t("vehicle.vehicleServiceContract.formNumber")}
                value={formNumber.field.value}
                onChange={handleFormNumberChange}
                onBlur={formNumber.field.onBlur}
                error={formNumber.fieldState.error}
                options={formNumberOptions}
                disabled={disabled}
              />
            )}
          </div>
        </form>
      </Form>
    </div>
  );
}
